import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const AnalysisProgressOverlay = ({ variant = 'domain', domain = '', isCompare = false, domainCount = 0 }) => {
  const { t } = useLanguage();
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  const domainSteps = [
    t('analysisProgress.connectingApi'),
    t('analysisProgress.downloadingCrux'),
    t('analysisProgress.analyzingLcp'),
    t('analysisProgress.analyzingCls'),
    t('analysisProgress.analyzingInp'),
    t('analysisProgress.calculatingScore'),
    t('analysisProgress.preparingReport'),
  ];

  const compareSteps = [
    t('analysisProgress.connectingApi'),
    t('analysisProgress.downloadingDomains'),
    t('analysisProgress.comparingMetrics'),
    t('analysisProgress.analyzingDifferences'),
    t('analysisProgress.calculatingScore'),
    t('analysisProgress.preparingReport'),
  ];

  const steps = isCompare ? compareSteps : domainSteps;

  useEffect(() => {
    setProgress(0);
    setStepIndex(0);
  }, [variant, domain, isCompare]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex(prev => {
        if (prev < steps.length - 1) return prev + 1;
        return prev;
      });
      setProgress(prev => {
        if (prev < 88) return prev + 6 + Math.random() * 6;
        return prev;
      });
    }, 2200);
    return () => clearInterval(interval);
  }, [steps.length]);

  const cleanDomain = domain?.replace(/^https?:\/\//, '').replace(/\/$/, '') || '';

  const iconColor = variant === 'compare' ? '#8b5cf6' : '#2563EB';
  const gradientFrom = variant === 'compare' ? '#8b5cf6' : '#2563EB';
  const gradientTo = variant === 'compare' ? '#06b6d4' : '#06b6d4';

  return (
    <div className="glass-card p-8 max-w-lg mx-auto my-8" style={{ animation: 'ap-fadeIn 0.3s ease-out' }}>
      <div className="flex flex-col items-center gap-6">
        <div className="relative w-24 h-24">
          <div className="absolute inset-0 rounded-full" style={{ border: `2px solid ${iconColor}30`, animation: 'ap-pulse 2s ease-in-out infinite' }} />
          <div className="absolute inset-2 rounded-full" style={{ borderWidth: 2, borderStyle: 'solid', borderColor: `${iconColor} transparent transparent transparent`, animation: 'ap-spin 1s linear infinite' }} />
          <div className="absolute inset-4 rounded-full" style={{ borderWidth: 2, borderStyle: 'solid', borderColor: `transparent ${gradientTo} transparent transparent`, animation: 'ap-spin 1.5s linear infinite reverse' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            {isCompare ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3h5v5M8 3H3v5M21 3l-7 7M3 3l7 7M21 21l-7-7M3 21l7-7M16 21h5v-5M8 21H3v-5" />
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            )}
          </div>
        </div>

        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-100 mb-1">
            {isCompare ? t('analysisProgress.comparingDomains') : t('analysisProgress.analyzingDomain')}
          </h3>
          {cleanDomain && !isCompare && (
            <p className="text-xs text-slate-500 mb-1 font-mono">{cleanDomain}</p>
          )}
          {isCompare && domainCount > 0 && (
            <p className="text-xs text-slate-500 mb-1">{domainCount} {t('analysisProgress.domains')}</p>
          )}
          <p className="text-sm font-mono" style={{ color: iconColor }}>{steps[stepIndex] || steps[0]}</p>
        </div>

        <div className="w-full">
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${Math.min(progress, 100)}%`,
                background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
                boxShadow: `0 0 10px ${gradientFrom}80`
              }}
            />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-slate-500">{Math.round(Math.min(progress, 100))}%</span>
            <span className="text-xs text-slate-500">~{isCompare ? '10–30s' : '5–15s'}</span>
          </div>
        </div>

        <div className="grid gap-1.5 w-full" style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}>
          {steps.map((step, i) => (
            <div
              key={i}
              className="h-1 rounded-full transition-all duration-300"
              style={{
                background: i < stepIndex ? '#10b981' : i === stepIndex ? `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})` : 'rgba(255,255,255,0.05)',
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ap-spin { to { transform: rotate(360deg); } }
        @keyframes ap-pulse { 0%, 100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.15); opacity: 0.6; } }
        @keyframes ap-fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AnalysisProgressOverlay;
